import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Save, Target } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  postColumnsMatching,
  getTargetMappingOptions,
  validateData,
  getDataProgress,
} from "../../api";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { useColumnMapping } from "./hooks/useColumnMapping";
import { FileSummaryCard } from "./FileSummaryCard";
import { ActionButtons } from "./ActionButtons";
import { ProgressBar } from "../ProgressBar";
import EnhancedColumnMappingTable from "./EnhanceColumnMappingTable";
import {
  useFuzzyMatch,
  useSuggestions,
  useUpdateMapping,
  useApplyTransformation,
  useRemoveTransformation,
  useSaveTemplate,
  useFinalizeStage2,
} from "../../hooks";
import { toSnakeCase } from "@shared/utils/caseConverter";
import { toast } from "@shared/hooks/use-toast";
import SaveTemplateDialog from "../data-saved/save-dialog/SaveTemplateDialog";
import { NavigationButtons } from "../shared/NavigationButtons";
import { Button } from "@shared/ui/button";
import { PageHeader } from "@shared/components/PageHeader";

const getMandatoryFieldsByDataType = (dataType: string): string[] => {
  const mandatoryFieldsMap: Record<string, string[]> = {
    Meters: [
      "meter_number",
      "utility_service",
      "category",
      "meter_type",
      "meter_make",
      "premise",
      "installed_on",
    ],
    Consumers: [
      "consumer_no",
      "first_name",
      "last_name",
      "email",
      "contact_number",
      "street",
      "ownership",
      "connect_date",
      "billing_premise",
      "zipcode",
      "Plan",
      "Category",
      "Status",
    ],
    "Consumer Meter Mapping": [
      "consumer_number",
      "meter_number",
      "utility_service",
    ],
    Payments: [
      "consumer_no",
      "amount",
      "payment_pay_type",
      "payment_mode",
      "payment_channel",
      "payment_type",
      "payment_subtype",
      "payment_date",
      "status",
    ],
  };

  return mandatoryFieldsMap[dataType] || [];
};
const convertColumnName = (columnName) => {
  if (!columnName) return columnName;

  // Check if the string contains spaces
  const hasSpaces = columnName.includes(" ");

  // If it has spaces, keep it as is
  if (hasSpaces) {
    return columnName;
  }

  // If no spaces, convert camelCase to snake_case
  // Insert underscore before uppercase letters and convert to lowercase
  return columnName.replace(/([a-z])([A-Z])/g, "$1_$2").toLowerCase();
};

const DataMappingPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const hasLogged = useRef(false);
  const [sourceAssignments, setSourceAssignments] = useState<
    Record<string, string>
  >({});
  const [sourceColumnOptions, setSourceColumnOptions] = useState<string[]>(
    state?.fileColumns || []
  );
  const templateId = state?.templateId; // ADD
  const uploadMethod = state?.uploadMethod; // ADD
  const isTemplateUpload = state?.isTemplateUpload || false; // ADD

  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [existingTemplateName, setExistingTemplateName] = useState<
    string | null
  >(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const [lastSavedMappings, setLastSavedMappings] = useState<string>("");
  const [isTemplateSaved, setIsTemplateSaved] = useState(false);
  const [transformationProgress, setTransformationProgress] = useState<
    Record<string, { percentage: number; status: string }>
  >({});

  const isSaveDisabled = isTemplateSaved && !hasUnsavedChanges;

  const convertedSourceColumnOptions =
    sourceColumnOptions.map(convertColumnName);
  // Universal helper to normalize column names for matching
  const normalizeColumnName = useCallback((name: string) => {
    if (!name) return "";
    return toSnakeCase(name.trim());
  }, []);

  // Helper to find matching source column from options (case-insensitive)
  const findMatchingSourceColumn = useCallback(
    (apiSourceColumn: string) => {
      if (!apiSourceColumn) return "";

      const normalizedApi = normalizeColumnName(apiSourceColumn);

      // Find exact match in CONVERTED source options
      const match = convertedSourceColumnOptions.find(
        (option) => normalizeColumnName(option) === normalizedApi
      );

      return match || ""; // Return empty string if no match found
    },
    [convertedSourceColumnOptions, normalizeColumnName]
  );

  const [loadingFields, setLoadingFields] = useState<Record<string, boolean>>(
    {}
  );
  const [isValidating, setIsValidating] = useState(false);

  const { mappings, setMappings } = useColumnMapping([]);

  // Hooks for API operations
  const saveTemplateMutation = useSaveTemplate();
  const finalizeStage2Mutation = useFinalizeStage2();

  const fuzzyMatchMutation = useFuzzyMatch();
  const updateMappingMutation = useUpdateMapping();
  const applyTransformationMutation = useApplyTransformation();
  const removeTransformationMutation = useRemoveTransformation();

  const getMappingsHash = useCallback(() => {
    const mappingData = mappings.map((m) => ({
      target: m.targetMapping,
      source: m.sourceColumn,
      transformation: m.transformationApplied,
      confidence: m.confidence,
    }));
    return JSON.stringify(mappingData);
  }, [mappings]);

  useEffect(() => {
    if (mappings.length > 0) {
      const currentMappingsHash = getMappingsHash();

      // Check if mappings have changed since last save
      if (lastSavedMappings && currentMappingsHash !== lastSavedMappings) {
        setHasUnsavedChanges(true);
        setIsTemplateSaved(false); // Re-enable save button when changes are made
      } else if (lastSavedMappings === currentMappingsHash) {
        setHasUnsavedChanges(false);
      }
    }
  }, [mappings, lastSavedMappings, getMappingsHash]);

  const {
    data: suggestionsData,

    refetch: refetchSuggestions,
  } = useSuggestions(state?.uploadId, !!state?.uploadId);

  // Initialize fuzzy match on mount
  useEffect(() => {
    // SKIP fuzzy match if this is a template upload
    if (isTemplateUpload || (uploadMethod === "template" && templateId)) {
      console.log("Template upload detected - skipping fuzzy match");
      return;
    }

    // Only trigger fuzzy match for independent uploads
    if (state?.uploadId && !state?.fuzzyMatchComplete) {
      fuzzyMatchMutation.mutate(
        {
          sessionId: state.uploadId,
          dataType: state.dataTypeKey || 1,
        },
        {
          onSuccess: (response) => {
            toast({
              title: `Fuzzy match started successfully`,
              description: "",
            });
          },
          onError: (error) => {
            toast({
              title: `Fuzzy match failed: ${error.message}`,
              description: "",
            });
          },
        }
      );
    }
  }, [state?.uploadId, isTemplateUpload, uploadMethod, templateId]);

  useEffect(() => {
    //I have made the snake_case variable as connectDate slips and was still in snake case thats why I have to handle this

    if (suggestionsData?.suggestions) {
      const transformedMappings = Object.entries(
        suggestionsData.suggestions
      ).map(([targetField, fieldData]) => {
        const apiSourceColumn =
          fieldData.sourceColumn ||
          fieldData.selectedSource ||
          fieldData.source_column ||
          fieldData.selected_source;

        return {
          targetMapping: targetField,
          sourceColumn: findMatchingSourceColumn(apiSourceColumn),
          confidence: fieldData.confidence,
          rawData: fieldData.rawData || fieldData.raw_data,
          transformedData:
            fieldData.transformedData || fieldData.transformed_data,
          sampleData: fieldData.sampleData || fieldData.sample_data,
          hasErrors: fieldData.hasErrors || fieldData.has_errors,
          errorCount: fieldData.errorCount || fieldData.error_count,
          allowedTransformations:
            fieldData.allowedTransformations ||
            fieldData.allowed_transformations,
          transformationApplied:
            fieldData.transformationApplied || fieldData.transformation_applied,
        };
      });

      setMappings(transformedMappings);

      // Build source assignments map
      const assignments: Record<string, string> = {};
      transformedMappings.forEach((m) => {
        if (m.sourceColumn) {
          assignments[m.sourceColumn] = m.targetMapping;
        }
      });
      setSourceAssignments(assignments);
    }
  }, [suggestionsData, setMappings]);

  const handleSourceColumnChange = useCallback(
    async (targetField: string, newSourceColumn: string) => {
      setIsTemplateSaved(false);

      // Find the old source column for this target field
      const oldSourceForThisTarget = Object.entries(sourceAssignments).find(
        ([source, target]) => target === targetField
      )?.[0];

      // Check if new source is already assigned elsewhere
      const previousAssignment = Object.entries(sourceAssignments).find(
        ([source, target]) =>
          source === newSourceColumn && target !== targetField
      );

      // Update local assignments optimistically
      setSourceAssignments((prev) => {
        const updated = { ...prev };

        // Remove old source assignment for this target field
        if (oldSourceForThisTarget) {
          delete updated[oldSourceForThisTarget];
        }

        // If the new source was assigned elsewhere, remove that assignment
        if (previousAssignment) {
          const [, oldTarget] = previousAssignment;
          // Clear the API mapping for the old target
          updateMappingMutation.mutate({
            sessionId: state.uploadId,
            targetField: oldTarget,
            sourceColumn: "",
            confidence: 0,
          });
        }

        // Add new assignment
        updated[newSourceColumn] = targetField;
        return updated;
      });

      // Update current field's mapping
      const existingMapping = mappings.find(
        (m) => m.targetMapping === targetField
      );
      const confidence = existingMapping?.confidence || 0.5;

      await updateMappingMutation.mutateAsync({
        sessionId: state.uploadId,
        targetField,
        sourceColumn: newSourceColumn,
        confidence,
      });
      await refetchSuggestions();
    },
    [
      sourceAssignments,
      state?.uploadId,
      mappings,
      updateMappingMutation,
      refetchSuggestions,
    ]
  );
  const handleSaveTemplate = async (
    templateName: string,
    action: "create" | "update"
  ) => {
    try {
      const response = await saveTemplateMutation.mutateAsync({
        sessionId: state.uploadId,
        templateName: templateName,
        description: `Template for ${state.selectedType} data migration`,
      });

      if (response) {
        // Store the current mappings configuration as "last saved"
        const currentMappingsHash = getMappingsHash();
        setLastSavedMappings(currentMappingsHash);

        // Update the existing template name if creating new
        if (action === "create") {
          setExistingTemplateName(templateName);
        }

        setHasUnsavedChanges(false);
        setIsTemplateSaved(true); // Mark as saved

        toast({
          title: "Template saved successfully",
          description: `Template "${templateName}" has been saved.`,
        });
      }
    } catch (error) {
      toast({
        title: `Failed to save template: ${error.message}`,
        description: "",
      });
    }
  };

  const handleApplyTransformation = useCallback(
    async (targetField: string, transformationConfig: any) => {
      setIsTemplateSaved(false);
      setLoadingFields((prev) => ({ ...prev, [targetField]: true }));

      const payload = {
        sessionId: state.uploadId,
        targetField: transformationConfig.targetField,
        transformation: transformationConfig.transformation,
      };

      if (transformationConfig.transformation.type == "no_transformation") {
        return;
      }

      await applyTransformationMutation.mutateAsync(payload, {
        onSuccess: () => {
          // Poll for transformation progress
          const pollInterval = setInterval(async () => {
            try {
              const progressData = await getDataProgress(state.uploadId);

              // Check if transformation is complete
              if (
                progressData.percentage >= 100 &&
                progressData.status === "completed"
              ) {
                clearInterval(pollInterval);

                // Now refetch suggestions to get updated data
                await refetchSuggestions();

                setLoadingFields((prev) => ({ ...prev, [targetField]: false }));

                toast({
                  title: "Transformation applied successfully",
                  description: "",
                });
              }
            } catch (error) {
              clearInterval(pollInterval);
              setLoadingFields((prev) => ({ ...prev, [targetField]: false }));
              toast({
                title: `Error polling progress: ${error.message}`,
                description: "",
              });
            }
          }, 2000);

          // Safety timeout after 30 seconds
          setTimeout(() => {
            clearInterval(pollInterval);
            setLoadingFields((prev) => ({ ...prev, [targetField]: false }));
            toast({
              title: "Transformation timeout",
              description: "Please refresh to see if changes were applied.",
            });
          }, 30000);
        },
        onError: (error) => {
          toast({
            title: `${error.message}`,
            description: "",
          });
          setLoadingFields((prev) => ({ ...prev, [targetField]: false }));
        },
      });
    },
    [state?.uploadId, applyTransformationMutation, refetchSuggestions]
  );

  const formattedMappingsForTemplate = useMemo(() => {
    return mappings
      .filter((m) => m.sourceColumn) // Only include mapped fields
      .map((mapping, index) => ({
        id: `mapping-${index}`,
        targetField: mapping.targetMapping,
        sourceColumn: mapping.sourceColumn || "",
        confidence: Math.round(mapping.confidence * 100), // Convert to percentage
        transformation:
          Array.isArray(mapping.transformationApplied) &&
          mapping.transformationApplied.length > 0
            ? mapping.transformationApplied[0].type
            : "none",
      }));
  }, [mappings]);

  // Helper to check if field is required
  const isFieldRequired = useCallback(
    (fieldName: string) => {
      const mandatoryFields = getMandatoryFieldsByDataType(
        state?.selectedType || ""
      );
      return mandatoryFields.includes(fieldName);
    },
    [state?.selectedType]
  );

  // Format mappings for display
  const formattedTargetFields = useMemo(() => {
    return mappings.map((mapping) => {
      return {
        name: mapping.targetMapping,
        isRequired: isFieldRequired(mapping.targetMapping),
        transformation:
          Array.isArray(mapping.transformationApplied) &&
          mapping.transformationApplied.length > 0
            ? mapping.transformationApplied[0].type
            : "no_transformation",
        sourceColumn: mapping.sourceColumn || mapping.selectedSource,
        confidence: mapping.confidence,
        rawData: mapping.rawData || [],
        transformedData: mapping.transformedData || [],
        sampleData: mapping.sampleData || [],
        allowedTransformations: mapping.allowedTransformations || [
          "trim_whitespace",
        ],
        hasErrors: mapping.hasErrors || false,
        isLoadingPreview: loadingFields[mapping.targetMapping] || false,
      };
    });
  }, [mappings, loadingFields, isFieldRequired]);

  // Navigation handlers
  const handleBackNavigation = () => {
    navigate(-1);
  };

  const handleContinue = async () => {
    try {
      setIsValidating(true);
      await finalizeStage2Mutation.mutateAsync(state.uploadId);

      const routePath = `/onboarding/data-migration/data-relationship-mapping/${state.selectedType}/${state.uploadId}`;
      navigate(routePath, {
        state: {
          migrationName: state.migrationName,
          selectedType: state.selectedType,
          fileName: state.fileName,
          uploadId: state.uploadId,
          operation_type: state.operation_type,
        },
      });
    } catch (error) {
      toast({
        title: `${error.message}`,
        description: "",
      });
    } finally {
      setIsValidating(false);
    }
  };

  const handleRemoveTransformation = useCallback(
    async (targetField: string, order: number) => {
      setIsTemplateSaved(false);
      setLoadingFields((prev) => ({ ...prev, [targetField]: true }));

      await removeTransformationMutation.mutateAsync(
        {
          sessionId: state.uploadId,
          targetField,
          order,
        },
        {
          onSuccess: () => {
            // Poll for transformation removal progress
            const pollInterval = setInterval(async () => {
              try {
                const progressData = await getDataProgress(state.uploadId);

                // Check if removal is complete
                if (
                  progressData.percentage >= 100 &&
                  progressData.status === "completed"
                ) {
                  clearInterval(pollInterval);

                  // Now refetch suggestions to get updated data
                  await refetchSuggestions();

                  setLoadingFields((prev) => ({
                    ...prev,
                    [targetField]: false,
                  }));

                  toast({
                    title: "Transformation removed successfully",
                    description: "",
                  });
                }
              } catch (error) {
                clearInterval(pollInterval);
                setLoadingFields((prev) => ({ ...prev, [targetField]: false }));
                toast({
                  title: `Error polling progress: ${error.message}`,
                  description: "",
                });
              }
            }, 2000);

            // Safety timeout after 30 seconds
            setTimeout(() => {
              clearInterval(pollInterval);
              setLoadingFields((prev) => ({ ...prev, [targetField]: false }));
              toast({
                title: "Operation timeout",
                description: "Please refresh to see if changes were applied.",
              });
            }, 30000);
          },
          onError: (error) => {
            toast({
              title: `${error.message}`,
              description: "",
            });
            setLoadingFields((prev) => ({ ...prev, [targetField]: false }));
          },
        }
      );
    },
    [state?.uploadId, removeTransformationMutation, refetchSuggestions]
  );

  const isLoading = mappings.length === 0;

  if (isLoading) {
    return (
      <div className="min-h-screen w-full p-6">
        <div className="w-full mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">
                {"AI is analyzing your columns and suggesting mappings..."}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-white p-6">
      <div className="w-full mx-auto">
        <ProgressBar
          currentStep={3}
          totalSteps={7}
          progress={42}
          currentStepName={"Data Mapping"}
        />

        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Target size={24} className="text-blue-600" />
            </div>
            <div className="flex flex-col mt-4">
              <PageHeader
                title="Column Mapping"
                subtitle="AI has analyzed your file and suggested column mappings. Review and confirm the mappings below."
              />
            </div>
          </div>
        </div>

        {/* File Summary Card */}

        {/* Mapping Table */}

        <EnhancedColumnMappingTable
          targetFields={formattedTargetFields}
          sourceColumnOptions={convertedSourceColumnOptions}
          onSourceColumnChange={handleSourceColumnChange}
          onApplyTransformation={handleApplyTransformation}
          sourceAssignments={sourceAssignments}
          onRemoveTransformation={handleRemoveTransformation}
          summary={null}
          dataMigrationName={state?.migrationName}
        />

        {/* Action Buttons */}
        <NavigationButtons
          onBack={handleBackNavigation}
          onContinue={handleContinue}
          continueText="Data Relation Mapping"
          isLoading={isValidating}
          additionalButtons={
            <Button
              onClick={() => setIsTemplateDialogOpen(true)}
              disabled={isSaveDisabled} // ✅ Now this works!
              className={`px-6 py-2 border rounded-lg transition-colors flex items-center gap-2 ${
                isSaveDisabled
                  ? "bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed"
                  : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
              }`}
              title={
                isSaveDisabled
                  ? "Template already saved. Make changes to save again."
                  : "Save as Template"
              }
            >
              <Save className="h-4 w-4" />
              Save as Template
              {hasUnsavedChanges && !isSaveDisabled && (
                <span className="ml-1 h-2 w-2 bg-orange-500 rounded-full" />
              )}
            </Button>
          }
        />

        <SaveTemplateDialog
          isOpen={isTemplateDialogOpen}
          onClose={() => setIsTemplateDialogOpen(false)}
          onSave={handleSaveTemplate}
          mappings={formattedMappingsForTemplate}
          fileName={state?.fileName || ""}
          existingTemplateName={existingTemplateName}
          hasUnsavedChanges={hasUnsavedChanges}
        />
      </div>
    </div>
  );
};

export default DataMappingPage;
